import type * as ld from "launchdarkly-js-client-sdk";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { retry } from "ts-retry-promise";

/** Logger for use by LaunchDarkly clients. */
export const ldLogger = {
  // Disable debug and info logging, as they are excessively noisy
  debug: () => {},
  info: () => {},
  warn: console.warn,
  error: console.error,
};

export type TypeOfResult =
  | "string"
  | "number"
  | "bigint"
  | "boolean"
  | "symbol"
  | "undefined"
  | "object"
  | "function";

const isLDUser = (context: ld.LDContext): context is ld.LDUser =>
  !("kind" in context);
const isLDSingleKindContext = (
  context: ld.LDContext,
): context is ld.LDSingleKindContext =>
  "kind" in context && context.kind !== "multi";

/**
 * True when the context is an anonymous user (single-kind or the `user`
 * kind of a multi-context). Anonymous identifies must not authorize flag
 * reads — on web the first auth identify is often a placeholder anon while
 * the profile is still loading.
 */
export const isAnonymousLdContext = (context: ld.LDContext): boolean => {
  if (isLDUser(context)) {
    return context.anonymous === true;
  }
  if (isLDSingleKindContext(context)) {
    return context.kind === "user" && context.anonymous === true;
  }
  // LDMultiKindContext index signature is `"multi" | LDContextCommon`.
  const user = context.user;
  return typeof user === "object" && user !== null && user.anonymous === true;
};

/**
 * The identity a context actually represents: the user key, or `null` when the
 * user kind is anonymous. Compared against the declared identity after an
 * identify lands, because `updateKind` enrichment builds its context from
 * whatever LaunchDarkly currently holds — so a succeeding enrichment can
 * confirm the *previous* identity while the batch was declared for a new one.
 */
export const ldContextIdentityKey = (context?: ld.LDContext): string | null => {
  if (!context || isAnonymousLdContext(context)) {
    return null;
  }
  if (isLDUser(context)) {
    return context.key ?? null;
  }
  if (isLDSingleKindContext(context)) {
    return context.kind === "user" ? (context.key ?? null) : null;
  }
  const user = context.user;
  return typeof user === "object" && user !== null ? (user.key ?? null) : null;
};

export type LDIdentifyOptions = {
  /**
   * When true, completing this identify authorizes flag reads (latches
   * `hasIdentified`). Defaults to `!isAnonymousLdContext(context)`.
   * Pass `true` for intentional logged-out anonymous audience targeting.
   */
  authorizeFlagReads?: boolean;
  /**
   * Who this identify makes current: `null` logged out, the user id when
   * signed in. Omit for same-user enrichment (e.g. adding a plan kind) to
   * keep the existing identity. See `declareFlagIdentity`.
   */
  identityKey?: string | null;
};

export type LDIdentifyFunction = (
  context: ld.LDContext | ((prevContext?: ld.LDContext) => ld.LDContext),
  options?: LDIdentifyOptions,
) => Promise<void>;

/** Add some context to existing LDContext, upgrading existing context into an
 * LDMultiContext if needed. */
export const updateKind =
  (multiUpdate: Omit<ld.LDMultiKindContext, "kind">) =>
  (prevContext?: ld.LDContext): ld.LDContext => {
    const base: ld.LDMultiKindContext = { kind: "multi", ...multiUpdate };
    if (!prevContext) {
      return base;
    } else if (isLDUser(prevContext)) {
      return { user: prevContext, ...base };
    } else if (isLDSingleKindContext(prevContext)) {
      const { kind: prevKind, ...prevRest } = prevContext;
      return { [prevKind]: prevRest, ...base };
    } else {
      // LDMultiContext
      return { ...prevContext, ...base };
    }
  };

export type FlagProviderProps = {
  children?: React.ReactNode;
  sdkKey?: string;
};

export type StartIdentifyOptions = {
  /** When true, this identify batch may latch `hasIdentified` on finish. */
  authorizeFlagReads?: boolean;
  /** Identity this batch makes current; omit for same-user enrichment. */
  identityKey?: string | null;
};

/**
 * Identity placeholder for the window where the session is authenticated but
 * the profile hasn't loaded, so there is no user id yet. Distinct from `null`
 * (logged out) so the transition still reads as an identity change.
 */
export const AUTHENTICATED_PENDING_IDENTITY = "authenticated-pending";

type FlagContextType = {
  /**
   * True once a non-anonymous identify has *succeeded* (or an intentional
   * logged-out anonymous identify opted in via `authorizeFlagReads`).
   * Flag *reads* must gate on this alone — never the UX floor.
   *
   * Monotonic except across an identity boundary: `declareFlagIdentity` drops
   * it until an identify succeeds for the new identity, because LaunchDarkly
   * keeps evaluating against the previous context until then.
   */
  hasIdentified: boolean;
  /**
   * True once the readiness floor timer has fired, or a read-authorizing
   * identify has landed. Monotonic — identity boundaries drop
   * `hasIdentified`, never this. Unblocks spinners; does **not** authorize
   * flag evaluation.
   */
  floorReached: boolean;
  /**
   * Bumps whenever a read-authorizing identify batch finishes. Web flag
   * memos depend on this so a later identify re-evaluates even when
   * `hasIdentified` is already true.
   */
  identifyGeneration: number;
  startIdentify: (options?: StartIdentifyOptions) => void;
  /**
   * `succeeded` reports whether LaunchDarkly actually accepted the identify.
   * A failed identify must not authorize reads — the SDK leaves `variation()`
   * on the previous context. `identifiedKey` is the identity LaunchDarkly
   * ended up on, checked against the one the batch was declared for.
   */
  finishIdentify: (succeeded: boolean, identifiedKey?: string | null) => void;
  declareFlagIdentity: (identityKey: string | null) => void;
};

export const FlagContext = createContext<FlagContextType>({
  hasIdentified: true,
  floorReached: true,
  identifyGeneration: 0,
  startIdentify: () => {},
  finishIdentify: () => {},
  declareFlagIdentity: () => {},
});

/**
 * Do not use this directly — prefer `useFlagHasIdentified` /
 * `useFlagContextIsReady` / `useFlagIdentifyGeneration`.
 * @deprecated
 */
export const useFlagContext = () => useContext(FlagContext);

/**
 * True once flag reads are authorized (a non-anonymous identify completed,
 * or an intentional logged-out anonymous identify). Monotonic.
 */
export const useFlagHasIdentified = () => useFlagContext().hasIdentified;

/**
 * True when first-paint UX may proceed: either flag reads are authorized, or
 * the readiness floor fired so we don't block the UI forever. Monotonic once
 * true — identity boundaries de-authorize reads without re-blocking this.
 *
 * Do **not** use this to gate `client.variation()` — use
 * `useFlagHasIdentified`.
 */
export const useFlagContextIsReady = () => {
  const { hasIdentified, floorReached } = useFlagContext();
  return hasIdentified || floorReached;
};

/** Generation counter for re-reading memoized flag values after identify. */
export const useFlagIdentifyGeneration = () =>
  useFlagContext().identifyGeneration;

/**
 * Declare who the app currently considers the user. Call it as soon as the
 * identity changes — including the login window where the session is
 * authenticated but the profile hasn't arrived, so no identify can run yet.
 * Reads stay de-authorized until an identify succeeds for that identity.
 */
export const useDeclareFlagIdentity = () =>
  useFlagContext().declareFlagIdentity;

// UX floor: unblock spinners if identify never authorizes reads (hung client,
// never-started identify). Must stay shorter-concern than LDProvider's init
// timeout — and must NEVER authorize flag reads on its own.
export const READY_TIMEOUT_MS = 2 * 1000;

// Tiny deferral so the ready-state update lands after the synchronous identify
// caller returns, allowing batched identify calls to coalesce.
export const FINISH_IDENTIFY_DEFER_MS = 10;

// Standard setup for native and web. Flag reads latch only when a batch
// finishes with `authorizeFlagReads` — anonymous placeholder identifies
// (auth waiting on profile email) must not unlock `variation()`.
export const useFlagContextSetup = () => {
  const [hasIdentified, setHasIdentified] = useState(false);
  const [floorReached, setFloorReached] = useState(false);
  const [identifyGeneration, setIdentifyGeneration] = useState(0);

  const markIdentified = useCallback(() => {
    setHasIdentified(true);
    setIdentifyGeneration(generation => generation + 1);
  }, []);

  // Floor timer: armed once at mount, never re-armed on identify. Extending
  // it per startIdentify made "2s floor" into "2s after last identify start".
  const readyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelReadyTimeout = useCallback(() => {
    if (readyTimeoutRef.current) {
      clearTimeout(readyTimeoutRef.current);
      readyTimeoutRef.current = null;
    }
  }, []);

  const identifyCountRef = useRef(0);
  const authorizeReadsBatchRef = useRef(false);
  const batchSucceededRef = useRef(false);
  // Identity the in-flight batch will authorize, and the one LaunchDarkly was
  // last successfully identified with. `undefined` means "never declared".
  const batchIdentityRef = useRef<string | null | undefined>(undefined);
  const batchIdentifiedKeyRef = useRef<string | null | undefined>(undefined);
  const currentIdentityRef = useRef<string | null | undefined>(undefined);
  const authorizedIdentityRef = useRef<string | null | undefined>(undefined);
  const pendingFinishTimersRef = useRef(
    new Set<ReturnType<typeof setTimeout>>(),
  );

  const declareFlagIdentity = useCallback((identityKey: string | null) => {
    if (currentIdentityRef.current === identityKey) {
      return;
    }
    currentIdentityRef.current = identityKey;
    if (authorizedIdentityRef.current !== identityKey) {
      setHasIdentified(false);
    }
  }, []);

  const startIdentify = useCallback((options?: StartIdentifyOptions) => {
    if (identifyCountRef.current === 0) {
      authorizeReadsBatchRef.current = false;
      batchSucceededRef.current = false;
      batchIdentityRef.current = currentIdentityRef.current;
    }
    if (options?.authorizeFlagReads) {
      authorizeReadsBatchRef.current = true;
    }
    if (options?.identityKey !== undefined) {
      batchIdentityRef.current = options.identityKey;
    }
    identifyCountRef.current++;
  }, []);

  const finishIdentify = useCallback(
    (succeeded: boolean, identifiedKey?: string | null) => {
      const timer = setTimeout(() => {
        pendingFinishTimersRef.current.delete(timer);
        identifyCountRef.current--;
        // Overlapping identifies coalesce into one batch, so `succeeded` and
        // `authorizeFlagReads` alone can't prove the *same* identify did both.
        // The identity check below is what closes that: it compares the key
        // LaunchDarkly ended up on against the one the batch was declared for.
        if (succeeded) {
          batchSucceededRef.current = true;
          batchIdentifiedKeyRef.current = identifiedKey;
        }
        if (identifyCountRef.current <= 0) {
          const declaredIdentity = batchIdentityRef.current;
          const confirmsDeclaredIdentity =
            declaredIdentity === undefined ||
            batchIdentifiedKeyRef.current === declaredIdentity;
          // The batch's identity is captured at start, so a batch that began
          // before the current identity was declared is stale by the time it
          // lands — LaunchDarkly is still on the old context. Latching on it
          // would reopen reads across the boundary we just closed.
          const isStillCurrentIdentity =
            currentIdentityRef.current === undefined ||
            declaredIdentity === currentIdentityRef.current;
          if (
            authorizeReadsBatchRef.current &&
            batchSucceededRef.current &&
            confirmsDeclaredIdentity &&
            isStillCurrentIdentity
          ) {
            authorizedIdentityRef.current = declaredIdentity;
            markIdentified();
            // Latch the floor too: readiness (hasIdentified || floorReached)
            // must be monotonic. A later identity boundary drops
            // hasIdentified, and with the floor timer cancelled below there
            // would otherwise be no timeout left to unblock first-paint gates.
            setFloorReached(true);
            cancelReadyTimeout();
          }
          authorizeReadsBatchRef.current = false;
          batchSucceededRef.current = false;
          batchIdentifiedKeyRef.current = undefined;
          identifyCountRef.current = 0;
        }
      }, FINISH_IDENTIFY_DEFER_MS);
      pendingFinishTimersRef.current.add(timer);
    },
    [cancelReadyTimeout, markIdentified],
  );

  // Mount-once UX floor.
  useEffect(() => {
    readyTimeoutRef.current = setTimeout(() => {
      console.warn("[Flag] Identify took too long, unblocking UX floor");
      setFloorReached(true);
    }, READY_TIMEOUT_MS);
    const pendingFinishTimers = pendingFinishTimersRef.current;
    return () => {
      cancelReadyTimeout();
      for (const timer of pendingFinishTimers) {
        clearTimeout(timer);
      }
      pendingFinishTimers.clear();
    };
  }, [cancelReadyTimeout]);

  return {
    hasIdentified,
    floorReached,
    identifyGeneration,
    startIdentify,
    finishIdentify,
    declareFlagIdentity,
  };
};

/**
 * Retry identifying the user to LaunchDarkly, with a timeout and retries.
 * Intended for internal use by the native and web implementations.
 *
 * Returns whether the identify landed. Callers must not authorize flag reads
 * when it didn't: the SDK keeps evaluating against the previous context, so a
 * swallowed failure would bucket the user on whoever came before. The timeout
 * doesn't abort the underlying call either — a late success can still move
 * LaunchDarkly's context, which is why reads stay on `defaultValue` rather
 * than on a value read from an identity we never confirmed.
 */
export const retryIdentify = async (identify: () => Promise<void>) => {
  try {
    await retry(identify, {
      timeout: 30 * 1000,
      retries: 2,
    });
    return true;
  } catch (error) {
    console.warn(`Failed to identify LaunchDarkly user: ${String(error)}`);
    return false;
  }
};
